import {
  useEffect,
  useCallback,
  useMemo,
} from "react";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { message } from "antd";

import {
  fetchSousProfiles,
  fetchSousProfileById,
  createSousProfile,
  addSousProfileEmail,
  updateSousProfile,
  deleteSousProfile,
  requestSousProfileSeparation,
  grantSousProfileAccess,
  revokeSousProfileAccess,
  verifyEmancipationToken,
  setEmancipationPassword,
  switchSousProfile,
  restoreMainProfile,
} from "../redux/thunks/sousProfileThunks";

import {
  selectSousProfiles,
  selectSousProfilesById,
  selectCurrentSousProfile,
  selectSousProfilesLoading,
  selectSousProfilesError,
  selectVerifyTokenStatus,
  selectVerifyTokenError,
  selectSetPasswordStatus,
  selectSetPasswordError,
  selectActingSession,
  selectIsActingAsSubprofile,
  selectActiveSubprofileId,
} from "../redux/selectors/sousProfileSelectors";

import { selectAuthUser } from "../../authPatient/redux/selectors/authSelectors.js";

/* ============================================================
   HELPERS
============================================================ */

const pickUserId = (user) =>
  user?.id ||
  user?._id ||
  user?.userId ||
  user?.uuid ||
  user?.patientId ||
  null;

const normalizeId = (value) => {
  if (value == null) {
    return null;
  }

  const id =
    String(value).trim();

  if (
    !id ||
    [
      "undefined",
      "null",
      "nan",
    ].includes(
      id.toLowerCase()
    )
  ) {
    return null;
  }

  return id;
};

/* ============================================================
   HOOK
============================================================ */

export default function useSousProfiles({
  autoFetch = true,
} = {}) {
  const dispatch =
    useDispatch();

  /* ==========================================================
     OWNER PRINCIPAL

     IMPORTANT :

     ownerId doit toujours être l'ID
     du compte patient principal.

     On n'utilise PAS selectEffectiveUserId
     pour ownerId.
  ========================================================== */

  const authUser =
    useSelector(
      selectAuthUser
    );

  const ownerId =
    useMemo(
      () =>
        normalizeId(
          pickUserId(
            authUser
          )
        ),

      [authUser]
    );

  /* ==========================================================
     REDUX STATE
  ========================================================== */

  const items =
    useSelector(
      selectSousProfiles
    );

  const byId =
    useSelector(
      selectSousProfilesById
    );

  const current =
    useSelector(
      selectCurrentSousProfile
    );

  const loading =
    useSelector(
      selectSousProfilesLoading
    );

  const error =
    useSelector(
      selectSousProfilesError
    );

  const verifyTokenStatus =
    useSelector(
      selectVerifyTokenStatus
    );

  const verifyTokenError =
    useSelector(
      selectVerifyTokenError
    );

  const setPasswordStatus =
    useSelector(
      selectSetPasswordStatus
    );

  const setPasswordError =
    useSelector(
      selectSetPasswordError
    );

  const actingSession =
    useSelector(
      selectActingSession
    );

  const isActingAsSubprofile =
    useSelector(
      selectIsActingAsSubprofile
    );

  const activeSubprofileId =
    useSelector(
      selectActiveSubprofileId
    );

  /* ==========================================================
     FETCH
  ========================================================== */

  const refetch =
    useCallback(() => {
      if (isActingAsSubprofile) {
        if (activeSubprofileId) {
          dispatch(fetchSousProfileById(activeSubprofileId));
        }
        return;
      }

      dispatch(fetchSousProfiles());
    }, [activeSubprofileId, dispatch, isActingAsSubprofile]);

  useEffect(() => {
    if (autoFetch) {
      refetch();
    }
  }, [autoFetch, refetch]);

  /* ==========================================================
     CREATE
  ========================================================== */

  const handleCreateSubprofile =
    useCallback(
      async (formValues) => {
        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        let type =
          "child";

        if (
          formValues.profileType ===
          "Dependent Parent"
        ) {
          type =
            "parent";
        }

        const payload = {
          type,

          firstName:
            (
              formValues.firstName ||
              ""
            ).trim(),

          lastName:
            (
              formValues.lastName ||
              ""
            ).trim(),

          birthdate:
            formValues.birthdate,

          gender:
            formValues.gender ||
            "not_specified",

          /*
           * IMPORTANT :
           * lié au compte patient principal.
           */
          linkedUserId:
            ownerId,
        };

        const action =
          await dispatch(
            createSousProfile(
              payload
            )
          );

        if (
          createSousProfile.fulfilled.match(
            action
          )
        ) {
          message.success(
            "Subprofile created successfully"
          );

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error while creating subprofile.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        ownerId,
      ]
    );

  /* ==========================================================
     ADD EMAIL
  ========================================================== */

  const handleAddSubprofileEmail =
    useCallback(
      async (
        id,
        email
      ) => {
        const action =
          await dispatch(
            addSousProfileEmail({
              id,
              email,
            })
          );

        if (
          addSousProfileEmail.fulfilled.match(
            action
          )
        ) {
          message.success(
            "Email updated successfully"
          );

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error while updating email.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
      ]
    );

  /* ==========================================================
     UPDATE (e.g. avatar)
  ========================================================== */

  const handleUpdateSubprofile =
    useCallback(
      async (id, fields) => {
        if (!id) {
          message.error(
            "Subprofile id not found"
          );

          return {
            ok: false,
            error:
              "Subprofile id not found",
          };
        }

        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        const action =
          await dispatch(
            updateSousProfile({
              id,
              ownerId,
              ...fields,
            })
          );

        if (
          updateSousProfile.fulfilled.match(
            action
          )
        ) {
          refetch();

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error updating subprofile.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        ownerId,
        refetch,
      ]
    );

  /* ==========================================================
     DELETE
  ========================================================== */

  const handleDeleteSubprofile =
    useCallback(
      async (id) => {
        if (!id) {
          message.error(
            "Subprofile id not found"
          );

          return {
            ok: false,
            error:
              "Subprofile id not found",
          };
        }

        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        const action =
          await dispatch(
            deleteSousProfile({
              id,
              ownerId,
            })
          );

        if (
          deleteSousProfile.fulfilled.match(
            action
          )
        ) {
          const activeId =
            actingSession
              ?.actingAs
              ?.id ||
            actingSession
              ?.actingAs
              ?._id ||
            actingSession
              ?.actingAs ||
            actingSession
              ?.profile
              ?.id ||
            actingSession
              ?.profile
              ?._id;

          if (
            isActingAsSubprofile &&
            activeId &&
            String(
              activeId
            ) ===
              String(
                id
              )
          ) {
            /*
             * Restore d'abord.
             * On garde actingSession afin que
             * axios puisse encore envoyer le token
             * du sous-profile.
             */
            await dispatch(
              restoreMainProfile({
                ownerId,
              })
            );

            try {
              localStorage.removeItem(
                "actingSession"
              );
            } catch {}
          }

          message.success(
            "Subprofile deleted successfully"
          );

          refetch();

          return {
            ok: true,
            data:
              action.payload
                ?.data,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error deleting subprofile.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        actingSession,
        dispatch,
        isActingAsSubprofile,
        ownerId,
        refetch,
      ]
    );

  /* ==========================================================
     REQUEST SEPARATION
  ========================================================== */

  const handleRequestSeparation =
    useCallback(
      async (id) => {
        const action =
          await dispatch(
            requestSousProfileSeparation(
              id
            )
          );

        if (
          requestSousProfileSeparation.fulfilled.match(
            action
          )
        ) {
          message.success(
            "Separation request started"
          );

          refetch();

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error requesting separation.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        refetch,
      ]
    );

  /* ==========================================================
     GRANT ACCESS
  ========================================================== */

  const handleGrantAccess =
    useCallback(
      async (
        id,
        userId,
        permission = "read"
      ) => {
        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        const action =
          await dispatch(
            grantSousProfileAccess({
              id,
              ownerId,
              userId,
              permission,
            })
          );

        if (
          grantSousProfileAccess.fulfilled.match(
            action
          )
        ) {
          message.success(
            "Guardian access granted"
          );

          refetch();

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error granting access.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        ownerId,
        refetch,
      ]
    );

  /* ==========================================================
     GRANT ACCESS EMAIL
  ========================================================== */

  const handleGrantAccessByEmail =
    useCallback(
      async (
        id,
        email,
        permission = "read"
      ) => {
        const normalizedEmail =
          String(
            email ||
            ""
          )
            .trim()
            .toLowerCase();

        if (
          !normalizedEmail
        ) {
          message.error(
            "Guardian email is required."
          );

          return {
            ok: false,
            error:
              "Guardian email is required.",
          };
        }

        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        const action =
          await dispatch(
            grantSousProfileAccess({
              id,
              ownerId,
              email:
                normalizedEmail,
              permission,
            })
          );

        if (
          grantSousProfileAccess.fulfilled.match(
            action
          )
        ) {
          const status =
            String(
              action.payload
                ?.status ||
              ""
            ).toLowerCase();

          message.success(
            status ===
              "invitation_sent"
              ? "Guardian invitation sent by email"
              : "Guardian access granted"
          );

          refetch();

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error granting access.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        ownerId,
        refetch,
      ]
    );

  /* ==========================================================
     REVOKE ACCESS
  ========================================================== */

  const handleRevokeAccess =
    useCallback(
      async (
        id,
        userId
      ) => {
        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        const action =
          await dispatch(
            revokeSousProfileAccess({
              id,
              ownerId,
              userId,
            })
          );

        if (
          revokeSousProfileAccess.fulfilled.match(
            action
          )
        ) {
          message.success(
            "Guardian access revoked"
          );

          refetch();

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error revoking access.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        ownerId,
        refetch,
      ]
    );

  /* ==========================================================
     VERIFY TOKEN
  ========================================================== */

  const handleVerifyToken =
    useCallback(
      async (token) => {
        const action =
          await dispatch(
            verifyEmancipationToken(
              token
            )
          );

        if (
          verifyEmancipationToken.fulfilled.match(
            action
          )
        ) {
          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error verifying emancipation token.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
      ]
    );

  /* ==========================================================
     PASSWORD
  ========================================================== */

  const handleSetPassword =
    useCallback(
      async (
        token,
        password
      ) => {
        const action =
          await dispatch(
            setEmancipationPassword({
              token,
              password,
            })
          );

        if (
          setEmancipationPassword.fulfilled.match(
            action
          )
        ) {
          message.success(
            "Password set successfully"
          );

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error setting emancipation password.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
      ]
    );

  /* ==========================================================
     SWITCH PROFILE
  ========================================================== */

  const handleSwitchProfile =
    useCallback(
      async (
        subprofileId
      ) => {
        if (
          !subprofileId
        ) {
          message.error(
            "Subprofile id not found"
          );

          return {
            ok: false,
            error:
              "Subprofile id not found",
          };
        }

        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        const action =
          await dispatch(
            switchSousProfile({
              subprofile_id:
                subprofileId,

              ownerId,
            })
          );

        if (
          switchSousProfile.fulfilled.match(
            action
          )
        ) {
          try {
            localStorage.setItem(
              "actingSession",

              JSON.stringify({
                mode:
                  "subprofile",

                accessToken:
                  action.payload
                    ?.accessToken ||
                  action.payload
                    ?.access_token ||
                  null,

                actingAs:
                  action.payload
                    ?.actingAs ||
                  action.payload
                    ?.acting_as ||
                  action.payload
                    ?.profile
                    ?.id ||
                  subprofileId,

                profile:
                  action.payload
                    ?.profile ||
                  null,
              })
            );
          } catch {}

          message.success(
            "Switched to subprofile"
          );

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error switching profile.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        ownerId,
      ]
    );

  /* ==========================================================
     RESTORE MAIN PROFILE
  ========================================================== */

  const handleRestoreProfile =
    useCallback(
      async () => {
        if (!ownerId) {
          message.error(
            "Owner user id not found"
          );

          return {
            ok: false,
            error:
              "Owner user id not found",
          };
        }

        const action =
          await dispatch(
            restoreMainProfile({
              ownerId,
            })
          );

        if (
          restoreMainProfile.fulfilled.match(
            action
          )
        ) {
          try {
            localStorage.removeItem(
              "actingSession"
            );
          } catch {}

          message.success(
            "Restored main profile"
          );

          return {
            ok: true,
            data:
              action.payload,
          };
        }

        let errMsg =
          action.payload ||
          action.error
            ?.message ||
          "Error restoring profile.";

        if (
          Array.isArray(
            errMsg
          )
        ) {
          errMsg =
            errMsg.join(
              ", "
            );
        }

        if (
          typeof errMsg !==
          "string"
        ) {
          errMsg =
            String(
              errMsg
            );
        }

        message.error(
          errMsg
        );

        return {
          ok: false,
          error:
            errMsg,
        };
      },

      [
        dispatch,
        ownerId,
      ]
    );

  /* ==========================================================
     RETURN
  ========================================================== */

  return {
    items,
    byId,
    current,
    loading,
    error,

    verifyTokenStatus,
    verifyTokenError,

    setPasswordStatus,
    setPasswordError,

    actingSession,
    isActingAsSubprofile,

    ownerId,

    refetch,

    handleCreateSubprofile,
    handleAddSubprofileEmail,
    handleUpdateSubprofile,
    handleDeleteSubprofile,
    handleRequestSeparation,

    handleGrantAccess,
    handleGrantAccessByEmail,
    handleRevokeAccess,

    handleVerifyToken,
    handleSetPassword,

    handleSwitchProfile,
    handleRestoreProfile,
  };
}